import React, { useState, useEffect } from 'react';
import { webhookLogsApi } from '../lib/dbApi';
import { logger } from '../lib/loggingService';

interface WebhookLog {
  id: string;
  payload: any;
  headers: string;
  ip_address: string;
  created_at: string;
  status: string;
  viewed?: boolean;
}

interface WebhookProps {
  userEmail: string;
}

const getClientName = (payload: any): string =>
  payload?.clientName || payload?.['Client Name'] || payload?.formData?.clientName || 'Unknown Client';

const getClientAddress = (payload: any): string =>
  payload?.clientAddress || payload?.['Client Address'] || payload?.formData?.clientAddress || '';

const getReportDate = (payload: any): string =>
  payload?.reportDate || payload?.['Report Date'] || '';

const getDownloadUrlFromPayload = (payload: any): string =>
  payload?.downloadUrl ||
  payload?.download_url ||
  payload?.reportDownloadUrl ||
  payload?.reportDownload_url ||
  payload?.reportDownloadURL ||
  payload?.report_download_url ||
  payload?.report_downloadUrl ||
  '';

const formatDate = (dateString: string): string => new Date(dateString).toLocaleString();

const Webhook: React.FC<WebhookProps> = ({ userEmail }) => {
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Only new (not yet acted on) webhooks belong here - once dismissed or edited,
  // they drop off this list.
  const fetchWebhookLogs = async () => {
    try {
      setError(null);
      const { data } = await webhookLogsApi.list({ viewed: false, limit: 50 });
      setWebhookLogs(data || []);
      await logger.logCustomEvent({
        username: userEmail,
        event_type: 'webhook_logs_viewed',
        event_category: 'report',
        event_details: { count: data?.length || 0 }
      });
    } catch (err: any) {
      console.error('Error fetching webhook logs:', err);
      setError(err.message || 'Failed to fetch webhook logs');
      await logger.logFrontendError(userEmail, 'WEBHOOK_FETCH_ERROR', err.message, err.stack);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhookLogs();
  }, [userEmail]);

  // Dismiss without editing - marks it viewed so it drops off this list.
  const handleDismiss = async (id: string) => {
    try {
      await webhookLogsApi.patch(id, { viewed: true });
      setWebhookLogs(prev => prev.filter(log => log.id !== id));
      await logger.logCustomEvent({
        username: userEmail,
        event_type: 'webhook_notification_dismissed',
        event_category: 'report',
        event_details: { webhookId: id }
      });
    } catch (err: any) {
      console.error('Error dismissing webhook log:', err);
      alert('Failed to dismiss webhook: ' + err.message);
    }
  };

  // Load this webhook's payload into the report generator.
  const handleEditAndGenerate = async (log: WebhookLog) => {
    try {
      await webhookLogsApi.patch(log.id, { viewed: true });
      setWebhookLogs(prev => prev.filter(l => l.id !== log.id));
      await logger.logCustomEvent({
        username: userEmail,
        event_type: 'webhook_notification_opened',
        event_category: 'report',
        event_details: { webhookId: log.id, clientName: getClientName(log.payload) }
      });
      localStorage.setItem('webhookData', JSON.stringify(log.payload));
      localStorage.setItem('webhookDataSource', JSON.stringify({ _webhookLogId: log.id }));
      window.dispatchEvent(new CustomEvent('app:navigate', { detail: { to: '/' } }));
    } catch (err: any) {
      console.error('Error opening webhook for editing:', err);
      alert('Failed to open webhook: ' + err.message);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Webhook Reports</h1>
          <p className="text-sm text-gray-500 mt-1">New webhook submissions waiting to be reviewed</p>
        </div>
        <button
          onClick={fetchWebhookLogs}
          disabled={isLoading}
          className="px-4 py-2 bg-[#722420] text-white rounded-lg hover:bg-[#8B2E26] transition-colors text-sm font-medium disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-gray-400">
          <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-[#722420] animate-spin" />
          <p className="text-sm">Loading webhooks…</p>
        </div>
      ) : webhookLogs.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-xl">
          <p className="text-gray-600 text-sm">No new webhooks</p>
          <p className="text-gray-400 text-xs mt-1">New submissions will show up here</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
          {webhookLogs.map((log) => {
            const clientName = getClientName(log.payload);
            const clientAddress = getClientAddress(log.payload);
            const reportDate = getReportDate(log.payload);
            const downloadUrl = getDownloadUrlFromPayload(log.payload);
            return (
              <div key={log.id} className="p-4">
                <div className="min-w-0 mb-2">
                  <div className="text-sm font-semibold text-gray-900 truncate">{clientName}</div>
                  {clientAddress && <div className="text-xs text-gray-500 truncate mt-0.5">{clientAddress}</div>}
                  <div className="text-xs text-gray-400 mt-1">{formatDate(log.created_at)}</div>
                </div>
                {reportDate && (
                  <div className="text-xs text-gray-500 mb-3">Report date: {reportDate}</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleEditAndGenerate(log)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#722420] text-white text-xs font-medium rounded-lg hover:bg-[#8B2E26] transition-colors"
                  >
                    Edit &amp; Generate
                  </button>
                  {downloadUrl && (
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Download PDF
                    </a>
                  )}
                  <button
                    onClick={() => handleDismiss(log.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors ml-auto"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Webhook;
